import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import apiClient from '../../api/apiClient';

// Map file extensions to language for syntax highlighting
const getLanguageFromExtension = (filename) => {
  const extension = filename.split('.').pop().toLowerCase();
  const extensionMap = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'tsx',
    py: 'python',
    html: 'html',
    css: 'css',
    json: 'json',
    md: 'markdown',
    sql: 'sql',
    sh: 'bash',
    bash: 'bash',
    txt: 'text',
    // Add more mappings as needed
  };
  return extensionMap[extension] || 'text';
};

const CodeBrowser = ({ repoPath, onFileSelected }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [fileTree, setFileTree] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the file tree on component mount
  useEffect(() => {
    if (repoPath) {
      fetchFileTree(repoPath);
    }
  }, [repoPath]);

  // When a file is selected, notify parent component
  useEffect(() => {
    if (selectedFile && onFileSelected) {
      onFileSelected(selectedFile);
    }
  }, [selectedFile, onFileSelected]);

  // Function to fetch the file tree from the backend
  const fetchFileTree = async (path) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/repo/files`, {
        params: { path }
      });
      
      setFileTree(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching file tree:", err);
      setError(`Failed to fetch file tree: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Function to fetch file content
  const fetchFileContent = async (filePath) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/repo/content`, {
        params: { path: filePath }
      });
      
      setCurrentContent(response.data.content);
      setSelectedFile(filePath);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching file content:", err);
      setError(`Failed to fetch file content: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Find the current directory in the file tree
  const getCurrentDirectory = () => {
    if (!fileTree) return null;
    
    // If we're at the root, return the entire tree
    if (!currentPath) return fileTree;
    
    // Otherwise, navigate to the current path
    let current = fileTree;
    const pathParts = currentPath.split('/').filter(Boolean);
    
    for (const part of pathParts) {
      if (!current.directories) return null;
      
      const nextDir = current.directories.find(dir => dir.name === part);
      if (!nextDir) return null;
      
      current = nextDir;
    }
    
    return current;
  };

  // Function to navigate to a directory
  const navigateToDirectory = (dirPath) => {
    setCurrentPath(dirPath);
    // Clear any selected file when changing directories
    setSelectedFile(null);
    setCurrentContent(null);
  };

  // Function to navigate up one directory
  const navigateUp = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop(); // Remove the last part
    const parentPath = pathParts.join('/');
    navigateToDirectory(parentPath);
  };

  // Render the current directory contents
  const renderDirectoryContents = () => {
    const currentDir = getCurrentDirectory();
    
    if (!currentDir) {
      return <div className="p-4 text-red-600">Directory not found</div>;
    }

    return (
      <div className="border rounded-md overflow-hidden">
        <div className="bg-gray-100 p-2 border-b flex items-center">
          <div className="font-medium">
            {currentPath ? `${repoPath}/${currentPath}` : repoPath}
          </div>
          {currentPath && (
            <button 
              onClick={navigateUp}
              className="ml-auto text-gray-600 hover:text-gray-900"
              title="Go up one directory"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" />
              </svg>
            </button>
          )}
        </div>
        <ul className="divide-y">
          {currentDir.directories && currentDir.directories.map((dir) => (
            <li 
              key={dir.name} 
              className="p-2 hover:bg-gray-50 cursor-pointer flex items-center"
              onClick={() => navigateToDirectory(currentPath ? `${currentPath}/${dir.name}` : dir.name)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              {dir.name}
            </li>
          ))}
          {currentDir.files && currentDir.files.map((file) => (
            <li 
              key={file.name} 
              className={`p-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between ${
                selectedFile === `${repoPath}/${currentPath}/${file.name}`.replace(/\/+/g, '/') ? 'bg-blue-50' : ''
              }`}
              onClick={() => {
                const fullPath = `${repoPath}/${currentPath}/${file.name}`.replace(/\/+/g, '/');
                fetchFileContent(fullPath);
              }}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                {file.name}
              </div>
              <span className="text-xs text-gray-500">{file.size}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Render file content with syntax highlighting
  const renderFileContent = () => {
    if (!selectedFile || !currentContent) {
      return null;
    }

    const fileName = selectedFile.split('/').pop();
    const language = getLanguageFromExtension(fileName);

    return (
      <div className="mt-4 border rounded-md overflow-hidden">
        <div className="bg-gray-100 p-2 border-b flex justify-between items-center">
          <div className="font-medium">{fileName}</div>
          <div className="flex">
            <button 
              onClick={() => navigator.clipboard.writeText(currentContent)}
              className="text-gray-600 hover:text-gray-900 p-1 rounded"
              title="Copy code"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
            </button>
            <button
              onClick={() => {
                if (onFileSelected) {
                  onFileSelected(`/analyze-file ${selectedFile}`);
                }
              }}
              className="text-green-600 hover:text-green-800 p-1 rounded ml-2"
              title="Analyze this file with AI"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
              </svg>
            </button>
          </div>
        </div>
        <SyntaxHighlighter 
          language={language} 
          style={nord}
          showLineNumbers={true}
          customStyle={{ margin: 0, maxHeight: '500px' }}
        >
          {currentContent}
        </SyntaxHighlighter>
      </div>
    );
  };

  return (
    <div className="code-browser">
      {error ? (
        <div className="p-4 text-red-600 border border-red-200 rounded-md bg-red-50">
          {error}
        </div>
      ) : loading && !fileTree ? (
        <div className="flex justify-center p-4">
          <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <div>
          {renderDirectoryContents()}
          {loading && selectedFile ? (
            <div className="mt-4 flex justify-center p-4 border rounded">
              <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            renderFileContent()
          )}
        </div>
      )}
    </div>
  );
};

export default CodeBrowser;